import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { linkTabs } from '../../tabHref';
import { defaultPnlRange, todayIso } from './statementMeta';
import type { StatementsOutletContext } from './statementsOutletContext';
import { STATEMENTS_TABS, resolveStatementsTabId } from './statementsTabsMeta';

/** Query keys the date range lives under, shared by both tabs. */
const FROM_PARAM = 'from';
const TO_PARAM = 'to';

/**
 * Statements shell state: which tab the URL selects, and the range both tabs
 * report over.
 *
 * The range lives in the query string for the same reasons it does in Books —
 * it survives a tab switch, a reload and a copied link, and a shared URL shows
 * the reader what the sender was looking at. Tab navigation carries it across.
 *
 * The default is the fiscal year to date, not the current month: that is what
 * a P&L is read over, and the Balance Sheet only reads the end of it either
 * way. It is the same default the standalone P&L screen had.
 */
export const useStatementsPage = () => {
    const { pathname } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedTabId = resolveStatementsTabId(pathname);

    const fallback = defaultPnlRange(todayIso());
    const fromDate = searchParams.get(FROM_PARAM) ?? fallback.from;
    const toDate = searchParams.get(TO_PARAM) ?? fallback.to;

    const onRangeChange = React.useCallback(
        (from: string, to: string) => {
            const next = new URLSearchParams(searchParams);
            next.set(FROM_PARAM, from);
            next.set(TO_PARAM, to);
            // `replace` so tweaking the range doesn't stack history entries —
            // Back should step between tabs, not between date edits.
            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams],
    );

    // Memoised so the open tab doesn't see a new context object — and refetch —
    // on every unrelated render of the shell.
    const outletContext: StatementsOutletContext = React.useMemo(
        () => ({ fromDate, toDate }),
        [fromDate, toDate],
    );

    // The shell's range rides along, so moving tabs does not
    // silently reset the window being read.
    const tabs = React.useMemo(
        () => linkTabs(STATEMENTS_TABS, searchParams),
        [searchParams],
    );

    return {
        tabs,
        selectedTabId,
        fromDate,
        toDate,
        onRangeChange,
        outletContext,
    };
};
